import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Text,
  useColorModeValue,
  VStack,
  Stack,
  Heading,
  HStack,
  Image,
  IconButton,
  Spinner,
} from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";
import { useMutation } from 'react-query';
import { forgotPassword, resendOTP, resetPassword, getCaptcha } from '../../services/authService';
import toast from 'react-hot-toast';

function ForgotPassword() {
  const textColor = useColorModeValue("gray.700", "white");
  const textColorSecondary = useColorModeValue("gray.400", "gray.400");

  const [step, setStep] = useState(1); // 1: phone, 2: new password
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [captchaSvg, setCaptchaSvg] = useState("");
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [captchaInput, setCaptchaInput] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");

  const captchaBorder = useColorModeValue("gray.200", "gray.500");
  const captchaBoxBg = useColorModeValue("gray.50", "gray.600");

  const fetchCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    try {
      const svgText = await getCaptcha();
      setCaptchaSvg(svgText.data);
      setCaptchaToken(svgText.token);
      setCaptchaInput("");
    } catch (e) {
      toast.error("خطا در بارگذاری کد امنیتی");
    } finally {
      setCaptchaLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCaptcha();
  }, [fetchCaptcha]);

  // Mutation برای ارسال OTP
  const forgotPasswordMutation = useMutation(forgotPassword, {
    onSuccess: (data) => {
      toast.success(data.message);
      setStep(2); // مستقیماً به مرحله رمز عبور جدید
      fetchCaptcha();
    },
    onError: (error) => {
      toast.error(error.message || 'خطا در ارسال کد تایید');
      fetchCaptcha();
    }
  });

  // Mutation برای ارسال مجدد OTP
  const resendOTPMutation = useMutation(resendOTP, {
    onSuccess: (data) => {
      toast.success(data.message);
      fetchCaptcha();
    },
    onError: (error) => {
      toast.error(error.message || 'خطا در ارسال مجدد کد تایید');
      fetchCaptcha();
    }
  });



  // Mutation برای تغییر رمز عبور
  const resetPasswordMutation = useMutation(resetPassword, {
    onSuccess: (data) => {
      toast.success(data.message);
      // انتقال به صفحه ورود
      window.location.href = '/#/auth/signin';
    },
    onError: (error) => {
      toast.error(error.message || 'خطا در تغییر رمز عبور');
    }
  });

  const handleSendOTP = () => {
    if (!phone) {
      toast.error('لطفاً شماره تلفن را وارد کنید');
      return;
    }
    if (!captchaInput.trim()) {
      toast.error('لطفاً کد امنیتی را وارد کنید');
      return;
    }
    forgotPasswordMutation.mutate({ phone, captchaInput, captchaToken });
  };

  const handleResendOTP = () => {
    if (!captchaInput.trim()) {
      toast.error('لطفاً کد امنیتی را وارد کنید');
      return;
    }
    resendOTPMutation.mutate({ phone, captchaInput, captchaToken });
  };



  const handleResetPassword = () => {
    if (!newPassword || !confirmPassword) {
      toast.error('لطفاً رمز عبور جدید و تکرار آن را وارد کنید');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('رمز عبور و تکرار آن یکسان نیستند');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('رمز عبور باید حداقل 6 کاراکتر باشد');
      return;
    }

    resetPasswordMutation.mutate({
      phone,
      otp,
      newPassword
    });
  };

  const handleBackToPhone = () => {
    setStep(1);
    setOtp("");
    setNewPassword("");
    setConfirmPassword("");
    fetchCaptcha();
  };



  return (
    <Flex minH="100vh" align="center" justify="center" bg={useColorModeValue("gray.50", "gray.900")}>
      <Stack spacing={8} mx="auto" maxW="lg" py={12} px={6}>
        <Stack align="center">
          <Image
            src="/logo.png"
            alt="لوگو مرکز مشاوره نارون سبز"
            boxSize="120px"
            objectFit="contain"
            mb={4}
          />
          <Heading fontSize="xl" textAlign="center">
            بازیابی رمز عبور
          </Heading>
        </Stack>
        
        <Box
          rounded="lg"
          bg={useColorModeValue("white", "gray.700")}
          boxShadow="lg"
          p={8}
        >
          <VStack spacing={6}>
            {/* Step 1: Phone Number */}
            {step === 1 && (
              <>
                <Text fontSize="lg" color={textColor} textAlign="center">
                  شماره تلفن خود را وارد کنید
                </Text>
                <FormControl>
                  <FormLabel>شماره تلفن</FormLabel>
                  <Input
                    type="tel"
                    placeholder="09123456789"
                    dir="ltr"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </FormControl>
                <FormControl id="captcha-step1">
                  <FormLabel>کد امنیتی</FormLabel>
                  <HStack spacing={2} align="start">
                    <Box
                      border="1px solid"
                      borderColor={captchaBorder}
                      borderRadius="md"
                      p={2}
                      bg={captchaBoxBg}
                      height="60px"
                      minW="160px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      overflow="hidden"
                      dangerouslySetInnerHTML={{ __html: captchaSvg }}
                    />
                    <IconButton
                      aria-label="تازه‌سازی کپچا"
                      icon={captchaLoading ? <Spinner size="sm" /> : <RepeatIcon />}
                      onClick={fetchCaptcha}
                      isLoading={captchaLoading}
                      variant="outline"
                      colorScheme="blue"
                      size="md"
                    />
                  </HStack>
                  <Input
                    mt={2}
                    placeholder="متن تصویر را وارد کنید"
                    value={captchaInput}
                    onChange={(e) => setCaptchaInput(e.target.value)}
                    autoComplete="off"
                  />
                </FormControl>
                <Button
                  type="submit"
                  colorScheme="blue"
                  size="lg"
                  fontSize="md"
                  onClick={handleSendOTP}
                  isLoading={forgotPasswordMutation.isLoading}
                  loadingText="در حال ارسال..."
                >
                  ارسال کد تایید
                </Button>
              </>
            )}

            {/* Step 2: New Password */}
            {step === 2 && (
              <>
                <Text fontSize="lg" color={textColor} textAlign="center">
                  رمز عبور جدید را وارد کنید
                </Text>
                <Text fontSize="sm" color={textColorSecondary} textAlign="center">
                  کد تایید به شماره {phone} ارسال شد
                </Text>
                
                <FormControl>
                  <FormLabel>کد تایید</FormLabel>
                  <Input
                    type="text"
                    placeholder="کد 6 رقمی را وارد کنید"
                    value={otp}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                      setOtp(value);
                    }}
                    maxLength={6}
                    dir="ltr"
                    textAlign="center"
                    fontSize="lg"
                  />
                </FormControl>

                <FormControl id="captcha-step2">
                  <FormLabel fontSize="sm">کد امنیتی (برای ارسال مجدد)</FormLabel>
                  <HStack spacing={2} align="start">
                    <Box
                      border="1px solid"
                      borderColor={captchaBorder}
                      borderRadius="md"
                      p={2}
                      bg={captchaBoxBg}
                      height="60px"
                      minW="160px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      overflow="hidden"
                      dangerouslySetInnerHTML={{ __html: captchaSvg }}
                    />
                    <IconButton
                      aria-label="تازه‌سازی کپچا"
                      icon={captchaLoading ? <Spinner size="sm" /> : <RepeatIcon />}
                      onClick={fetchCaptcha}
                      isLoading={captchaLoading}
                      variant="outline"
                      colorScheme="blue"
                      size="md"
                    />
                  </HStack>
                  <Input
                    mt={2}
                    placeholder="متن تصویر را وارد کنید"
                    value={captchaInput}
                    onChange={(e) => setCaptchaInput(e.target.value)}
                    autoComplete="off"
                  />
                </FormControl>

                <Button
                  variant="link"
                  color="blue.500"
                  fontSize="sm"
                  onClick={handleResendOTP}
                  isLoading={resendOTPMutation.isLoading}
                  isDisabled={resendOTPMutation.isLoading}
                >
                  ارسال مجدد کد تایید
                </Button>

                <FormControl>
                  <FormLabel>رمز عبور جدید</FormLabel>
                  <Input
                    type="password"
                    placeholder="رمز عبور جدید"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>تکرار رمز عبور</FormLabel>
                  <Input
                    type="password"
                    placeholder="تکرار رمز عبور"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </FormControl>

                <HStack spacing={4} w="100%">
                  <Button
                    variant="outline"
                    size="lg"
                    fontSize="md"
                    flex={1}
                    onClick={handleBackToPhone}
                  >
                    بازگشت
                  </Button>
                  <Button
                    colorScheme="blue"
                    size="lg"
                    fontSize="md"
                    flex={1}
                    onClick={handleResetPassword}
                    isLoading={resetPasswordMutation.isLoading}
                    loadingText="در حال تغییر..."
                  >
                    تغییر رمز عبور
                  </Button>
                </HStack>
              </>
            )}



                         {/* Back to Login */}
             <Button
               fontSize="10px"
               variant="link"
               color={textColorSecondary}
               fontWeight="normal"
               onClick={() => window.location.href = '/#/auth/signin'}
             >
               بازگشت به صفحه ورود
             </Button>
           </VStack>
         </Box>
       </Stack>
     </Flex>
  );
}

export default ForgotPassword;
